/* Las novedades: qué pasó y qué hay que hacer.
 *
 * NO HAY UNA TABLA `novedades`, Y ES LA DECISIÓN QUE SOSTIENE TODO ESTO. Una
 * tabla haría falta escribirla, y nadie escribe una novedad: la pantalla
 * quedaría vacía a la semana y ahí deja de mirarse para siempre. Las tarjetas
 * se DERIVAN de datos que ya existen y que se actualizan solos, así que la
 * pantalla está viva desde el primer día sin que nadie la alimente. Es la
 * misma idea que la lista de "Cómo se usa", que se arma desde `SECCIONES` en
 * vez de escribirse a mano.
 *
 * TRES REGLAS, Y CADA UNA EVITA UN MODO DE MORIR:
 *
 *   1. Una tarjeta sólo aparece si la persona PUEDE ABRIR la pantalla a la que
 *      lleva. Una novedad que apunta a algo que no se puede abrir es peor que
 *      no tenerla: avisa de un problema y esconde la salida.
 *
 *   2. Sin nada que decir, la tarjeta NO APARECE. Un "0 tickets pendientes" no
 *      es una novedad, es ruido — y peor, se lee como un aviso hasta que uno
 *      mira bien. Misma regla que `ocultarEnCero` en el menú.
 *
 *   3. Lo viejo no es una novedad. Hay 162 RTO vencidos de 2016 en el padrón:
 *      contarlos haría que la tarjeta diga 275 y tape los 113 que sí se
 *      resuelven esta semana. `RTO.jsx` ya usa este corte y por eso el número
 *      vive acá, compartido — si cada pantalla tuviera el suyo, dirían cosas
 *      distintas del mismo dato.
 */

/* Un vencimiento de hace más de tres meses no se resuelve esta semana: casi
 * siempre es una unidad que ya no rueda y que nadie dio de baja. Es el mismo
 * valor que usa la pantalla de RTO para separar lo accionable de lo
 * arqueológico. */
import { seccion } from "../components/layout/secciones";
import { DIAS_AVISO } from "../shared/rto";

export const DIAS_VIEJO = 90;

/* Con menos de esto encima, el RTO deja de ser un recordatorio y pasa a ser el
 * trabajo de la semana. */
export const DIAS_URGENTE = 15;

/* Un tramo con salida y sin llegada de hace más de dos semanas no es un viaje
 * en curso: es planilla vieja sin cerrar. Hay 268 abiertos y 260 salieron hace
 * más de eso — contarlos haría que la tarjeta diga 268 y tape los 8 que sí se
 * resuelven hoy.
 *
 * Es el mismo techo con el que `movimientos.candidato()` de FlotaBot se niega a
 * cerrar un tramo viejo, y la misma idea que `DIAS_VIEJO` acá arriba: lo que no
 * se resuelve esta semana no es una novedad. */
export const DIAS_TRAMO_ABIERTO = 15;

/* Cuántos días atrás cuenta como "recién". Es lo que separa la NOTICIA del
 * stock: hoy hay 107 RTO de tractores vencidos, y 20 se vencieron esta semana.
 *
 * SIN EL SEGUNDO NÚMERO LA TARJETA DICE LO MISMO TODOS LOS DÍAS. Un 107 que
 * dentro de un mes va a decir 103 no informa nada; el que cambió es el que
 * manda a hacer algo. Es la misma idea que `comparar_con_ayer` del extracto de
 * AdBlue: la novedad es lo que se movió, y la foto es el contexto.
 *
 * Se calcula desde la fecha de vencimiento y no guardando la foto de ayer: el
 * dato ya está, y una tabla de snapshots es una cosa más que puede quedar sin
 * escribirse. */
export const DIAS_NUEVO = 7;

/* Cuenta vencidos y urgentes de una lista de RTO, con el corte de lo viejo.
 *
 * Lo comparten tractores y furgones: son dos padrones distintos y la misma
 * pregunta, así que con dos copias el día que se toque el criterio una de las
 * dos tarjetas quedaría contando otra cosa — y las dos se leen igual.
 */
function contarRTO(filas) {
  const vencidas = filas.filter(
    (r) => r.estado === "vencida" && Math.abs(r.dias ?? 0) <= DIAS_VIEJO,
  );
  const vencidos = vencidas.length;
  // Los que cruzaron la fecha esta semana. Ver `DIAS_NUEVO`.
  const nuevos = vencidas.filter(
    (r) => (r.dias ?? -999) >= -DIAS_NUEVO && (r.dias ?? -999) <= 0,
  ).length;
  const urgentes = filas.filter(
    (r) => r.estado === "por_vencer" && (r.dias ?? 999) <= DIAS_URGENTE,
  ).length;
  const porVencer = filas.filter((r) => r.estado === "por_vencer").length;
  return { vencidos, urgentes, porVencer, nuevos };
}

/* Arma las tarjetas. Pura: recibe y ordena, no consulta nada.
 *
 *   · `conteos`      — lo que ya trae el armazón para los números del menú
 *   · `rto`          — filas de `rto_pendientes()` (tractores)
 *   · `rtoFurgones`  — filas de `rto_furgones()`
 *   · `puede(id)`    — si la persona puede abrir esa sección del menú
 *
 * El orden NO es por tamaño del número: primero lo que vence, después lo que
 * espera respuesta, y al final lo informativo. Un padrón con 1.300 filas
 * ganaría siempre por número y no hay nada que hacer con él.
 */
export function armarNovedades({
  conteos = {},
  rto = [],
  rtoFurgones = [],
  puede = () => true,
}) {
  const tarjetas = [];
  const n = (x) => (typeof x === "number" && x > 0 ? x : 0);

  // ── Lo que vence ──────────────────────────────────────────────────────────
  if (puede("rto")) {
    const { vencidos, urgentes, porVencer, nuevos } = contarRTO(rto);

    if (vencidos) {
      tarjetas.push({
        id: "rto-vencidos",
        seccion: "rto",
        tono: "urgente",
        titulo: `${vencidos} ${vencidos === 1 ? "RTO vencido" : "RTO vencidos"}`,
        detalle:
          "No pueden salir así. Los vencidos hace más de tres meses no se " +
          "cuentan acá: son unidades que no ruedan.",
        cuanto: vencidos,
        nuevos,
        ruta: "/rto",
        accion: "Ver cuáles",
      });
    }
    if (urgentes) {
      tarjetas.push({
        id: "rto-urgentes",
        seccion: "rto",
        tono: "aviso",
        titulo: `${urgentes} ${urgentes === 1 ? "RTO vence" : "RTO vencen"} en ${DIAS_URGENTE} días`,
        detalle:
          porVencer > urgentes
            ? `De ${porVencer} que vencen este mes, estos son los que no dan espera.`
            : "Hay que sacar el turno antes de que salgan.",
        cuanto: urgentes,
        ruta: "/rto",
        accion: "Sacar turno",
      });
    }
  }

  /* EL RTO DE LOS ACOPLADOS ES OTRA TARJETA, no un número sumado al de los
   * tractores. Son dos padrones, dos pantallas y dos personas resolviéndolos;
   * un total junto obligaría a abrir las dos para saber dónde está el trabajo.
   * Y el acoplado vencido frena la salida igual que el camión. */
  if (puede("rto_furgones")) {
    const { vencidos, urgentes, nuevos } = contarRTO(rtoFurgones);
    if (vencidos) {
      tarjetas.push({
        id: "rto-furgones-vencidos",
        seccion: "rto_furgones",
        tono: "urgente",
        titulo: `${vencidos} ${vencidos === 1 ? "acoplado vencido" : "acoplados vencidos"} de RTO`,
        detalle:
          "No pueden salir así. Los vencidos hace más de tres meses no se " +
          "cuentan acá: son unidades que no ruedan.",
        cuanto: vencidos,
        nuevos,
        ruta: "/furgones-rto",
        accion: "Ver cuáles",
      });
    }
    if (urgentes) {
      tarjetas.push({
        id: "rto-furgones-urgentes",
        seccion: "rto_furgones",
        tono: "aviso",
        titulo: `${urgentes} ${urgentes === 1 ? "acoplado vence" : "acoplados vencen"} en ${DIAS_URGENTE} días`,
        detalle: "El turno se saca en la misma agenda que la de los camiones.",
        cuanto: urgentes,
        ruta: "/rto?vista=agenda",
        accion: "Sacar turno",
      });
    }
  }

  /* LA VENTANA ENTERA DE 30 DÍAS, EN UNA SOLA TARJETA (pedido de Aldo, 01/09).
   *
   * Las dos tarjetas de urgentes cuentan lo que no da espera (15 días); ésta
   * cuenta lo mismo que el mail diario de RTO: TODO lo que entra en la ventana
   * de 30 días, camiones y acoplados juntos. La superposición es deliberada —
   * contestan preguntas distintas: «qué hago hoy» y «qué viene» — y el que la
   * pidió quería justamente ver venir el mes entero sin abrir el mail. */
  {
    const t30 = puede("rto")
      ? rto.filter((r) => r.estado === "por_vencer")
      : [];
    const f30 = puede("rto_furgones")
      ? rtoFurgones.filter((r) => r.estado === "por_vencer")
      : [];
    const total30 = t30.length + f30.length;
    if (total30) {
      const partes = [];
      if (puede("rto")) partes.push(`${t30.length} de camiones`);
      if (puede("rto_furgones")) partes.push(`${f30.length} de acoplados`);
      // Los que ENTRARON a la ventana esta semana: hace 7 días les quedaba
      // más de un mes. Sin este número la tarjeta dice lo mismo todos los días.
      const nuevos = [...t30, ...f30].filter(
        (r) => (r.dias ?? 0) > DIAS_AVISO - DIAS_NUEVO,
      ).length;
      tarjetas.push({
        id: "vencen-30",
        seccion: puede("rto") ? "rto" : "rto_furgones",
        tono: "aviso",
        titulo: `Vencimientos a ${DIAS_AVISO} días`,
        detalle:
          `${partes.join(" y ")}. La misma ventana que el aviso diario ` +
          "por mail: lo que hay que ir turneando este mes.",
        cuanto: total30,
        nuevos,
        ruta: puede("rto") ? "/rto" : "/furgones-rto",
        accion: "Ver cuáles",
      });
    }
  }

  /* LA DOCUMENTACIÓN DE LOS CHOFERES, con el mismo criterio con el que avisa
   * FlotaBot. No es una lista de papeles: es qué frena una salida.
   *
   * EL PSICOFÍSICO NO ESTÁ, y no es un olvido: hay 142 vencidos de 153. Una
   * tarjeta con ese número tapa todo lo demás y a la semana nadie mira la
   * pantalla. Hace falta para renovar el registro, no para salir a la ruta —
   * MOSTRAR no es AVISAR, y ese criterio ya vive en `docs_que_avisan()` de
   * FlotaBot. Si acá dijéramos otra cosa, el hub y el mail de los lunes
   * contarían distinto el mismo dato. */
  if (puede("documentos")) {
    const registro = n(conteos.docs_registro_vencido);
    const pronto = n(conteos.docs_registro_pronto);
    const peligrosas = n(conteos.docs_peligrosas);

    if (registro) {
      tarjetas.push({
        id: "docs-registro",
        seccion: "documentos",
        tono: "urgente",
        titulo: `${registro} ${registro === 1 ? "chofer con el registro vencido" : "choferes con el registro vencido"}`,
        detalle: "Sin registro al día no pueden manejar.",
        cuanto: registro,
        ruta: "/documentacion",
        accion: "Ver quiénes",
      });
    }
    if (peligrosas) {
      tarjetas.push({
        id: "docs-peligrosas",
        seccion: "documentos",
        tono: "urgente",
        titulo: `${peligrosas} sin cargas peligrosas al día`,
        detalle:
          "Cuenta sólo a los que hacen cargas peligrosas: pedírselo a los " +
          "demás llenaría la lista de avisos que no hay que atender.",
        cuanto: peligrosas,
        ruta: "/documentacion",
        accion: "Ver quiénes",
      });
    }
    if (pronto) {
      tarjetas.push({
        id: "docs-registro-pronto",
        seccion: "documentos",
        tono: "aviso",
        titulo: `${pronto} ${pronto === 1 ? "registro vence" : "registros vencen"} este mes`,
        detalle: "Se renuevan antes de que frenen una salida.",
        cuanto: pronto,
        ruta: "/documentacion",
        accion: "Ver quiénes",
      });
    }
  }

  /* VIAJES CON SALIDA Y SIN LLEGADA. Un tramo abierto no es un error: puede ser
   * un camión andando. Lo que lo hace una novedad es que **se ve y se cierra**,
   * y el que no se cierra queda para siempre midiendo mal la duración de esa
   * ruta.
   *
   * Sólo los de los últimos 15 días: de los 268 abiertos, 260 salieron hace más
   * de eso y son planilla vieja. Contarlos taparía los 8 de hoy. */
  const tramos = n(conteos.tramos_abiertos);
  if (tramos && puede("movimientos")) {
    tarjetas.push({
      id: "tramos-abiertos",
      seccion: "movimientos",
      tono: "aviso",
      titulo: `${tramos} ${tramos === 1 ? "viaje sin fecha de llegada" : "viajes sin fecha de llegada"}`,
      detalle:
        `De los últimos ${DIAS_TRAMO_ABIERTO} días. Los más viejos no se ` +
        "cuentan acá: ésos ya no son viajes en curso.",
      cuanto: tramos,
      ruta: "/panel/movimientos",
      accion: "Cerrarlos",
    });
  }

  // ── Lo que espera respuesta ───────────────────────────────────────────────
  const tickets = n(conteos.tickets);
  if (tickets && puede("tickets")) {
    tarjetas.push({
      id: "tickets",
      seccion: "tickets",
      tono: "aviso",
      titulo: `${tickets} ${tickets === 1 ? "ticket sin resolver" : "tickets sin resolver"}`,
      /* EL DETALLE DICE POR QUÉ TE APARECE A VOS, y antes decía otra cosa:
       * «pedidos que alguien anotó y todavía nadie cerró» describía el estado
       * del ticket y no tu relación con él — y la cuenta hacía lo mismo, así
       * que a alguien le podían aparecer tres pedidos que otra persona ya
       * estaba resolviendo. `tickets_pendientes()` ahora cuenta sólo lo que
       * te toca; la frase tiene que decirlo o el número se sigue leyendo como
       * «todo lo abierto». */
      detalle:
        "Los que te tocan: asignados a vos, de un área que atendés y que " +
        "nadie tomó todavía, o que pediste vos y siguen esperando.",
      cuanto: tickets,
      ruta: "/tickets",
      accion: "Abrir",
    });
  }

  const mensajes = n(conteos.mensajes);
  if (mensajes && puede("mensajes")) {
    tarjetas.push({
      id: "mensajes",
      seccion: "mensajes",
      tono: "aviso",
      titulo: `${mensajes} ${mensajes === 1 ? "mensaje sin leer" : "mensajes sin leer"}`,
      detalle: "Te escribió alguien de la empresa.",
      cuanto: mensajes,
      ruta: "/mensajes",
      accion: "Leer",
    });
  }

  return tarjetas;
}

/* ─────────────────────────────────────────────────────────────────────────
 * AGRUPAR POR SECTOR
 *
 * Con diez tarjetas planas, los 107 RTO vencidos y tus 3 tickets pesan lo
 * mismo en la grilla: el que abre a la mañana no sabe cuál es suyo. Agrupadas
 * por universo, cada uno encuentra su bloque de un vistazo — y es el MISMO
 * vocabulario que el menú, así que no hay nada nuevo que aprender.
 *
 * ── «PARA VOS» VA PRIMERO, Y ESO REFINA LA REGLA DE ORDEN ────────────────
 *
 * El orden de las tarjetas es «primero lo que vence, después lo que espera
 * respuesta», y sigue valiendo **adentro de cada bloque**. Entre bloques manda
 * otra cosa: los tickets y los mensajes son los únicos que son de la persona
 * que está mirando, y el RTO de la flota es el trabajo de Marcos. Con el orden
 * plano, dos mensajes sin leer quedaban abajo de 107 vencimientos que esa
 * persona no va a resolver.
 *
 * Los dos ya viven en el universo «Novedades» del menú, así que el bloque no
 * es una categoría inventada: es ese universo, dicho con la palabra que
 * corresponde cuando lo que se muestra son tus pendientes.
 *
 * ── LOS TÍTULOS SALEN DEL CATÁLOGO ───────────────────────────────────────
 *
 * De `seccion(id)`, la misma lista que dibuja el menú. Escritos a mano, el día
 * que un sector se renombre la home diría «Comercial» y el rail otra cosa — y
 * eso ya pasó una vez en este repo.
 * ───────────────────────────────────────────────────────────────────────── */

export const PARA_VOS = "Para vos";

/* El universo cuyas tarjetas son de quien está mirando. */
const UNIVERSO_PROPIO = "Novedades";

export function agrupar(tarjetas = [], buscar = seccion) {
  const orden = [];
  const porClave = new Map();

  for (const t of tarjetas) {
    const item = t.seccion ? buscar(t.seccion) : null;
    /* Una tarjeta sin universo NO se descarta: va a su propio bloque al final.
     * Descartarla sería perder una novedad por un dato de catálogo que falta,
     * y el síntoma —una tarjeta que dejó de aparecer— no diría por qué. */
    const universo = item?.universo || "Otras";
    const clave = universo === UNIVERSO_PROPIO ? PARA_VOS : universo;
    if (!porClave.has(clave)) {
      porClave.set(clave, { clave, titulo: clave, tarjetas: [] });
      orden.push(clave);
    }
    porClave.get(clave).tarjetas.push(t);
  }

  /* «Para vos» primero; el resto en el orden en que aparecieron, que es el del
   * service: lo que vence antes que lo que espera respuesta. */
  return orden
    .sort((a, b) => (a === PARA_VOS ? -1 : b === PARA_VOS ? 1 : 0))
    .map((c) => porClave.get(c));
}

/* Qué decir cuando no hay ninguna tarjeta.
 *
 * SE DICE, no se deja la pantalla vacía. "No hay nada pendiente" y "esto no
 * cargó" se ven exactamente igual en una pantalla en blanco, y son cosas muy
 * distintas: una tranquiliza y la otra hay que reportarla.
 */
export const SIN_NOVEDADES = {
  titulo: "No hay nada pendiente",
  detalle:
    "Ni RTO encima, ni documentación vencida, ni viajes sin cerrar, ni " +
    "tickets abiertos, ni mensajes sin leer. Si esperabas ver algo acá, " +
    "avisá — puede ser un permiso que falta.",
};
